import { useEffect, useRef, type RefObject } from "react";

import type { LeadsSearchFilterKey } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";

type Params = {
    scrollContainerRef: RefObject<HTMLElement | null>;
    dirtyFilterKeys: readonly LeadsSearchFilterKey[];
    getSectionElement: (key: string) => HTMLElement | null;
};

const SECTION_MOUNT_TIMEOUT_MS = 2000;

const isElementFullyVisibleInContainer = (element: Element, container: HTMLElement): boolean => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
};

const scrollElementIntoContainerView = (element: Element, container: HTMLElement): void => {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (elementRect.top < containerRect.top) {
        const delta = elementRect.top - containerRect.top;
        container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
        return;
    }

    if (elementRect.bottom > containerRect.bottom) {
        const delta = elementRect.bottom - containerRect.bottom;
        container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
    }
};

/**
 * Scrolls the Selected preview panel when a new filter section appears, without
 * scrolling parent layouts on mount, edit, or clear.
 */
export const useScrollToNewlyAddedFilter = ({
    scrollContainerRef,
    dirtyFilterKeys,
    getSectionElement,
}: Params): void => {
    const previousKeysRef = useRef<readonly string[] | null>(null);
    const dirtyFilterKeysRef = useRef(dirtyFilterKeys);
    dirtyFilterKeysRef.current = dirtyFilterKeys;
    const dirtyFilterKeysSerialized = dirtyFilterKeys.map(String).join("|");

    useEffect(() => {
        const currentKeys = dirtyFilterKeysRef.current.map(String);
        const previousKeys = previousKeysRef.current;

        if (previousKeys === null) {
            previousKeysRef.current = currentKeys;
            return undefined;
        }

        const previousKeySet = new Set(previousKeys);
        const newlyAddedKeys = currentKeys.filter((key) => !previousKeySet.has(key));

        previousKeysRef.current = currentKeys;

        if (newlyAddedKeys.length === 0) {
            return undefined;
        }

        const lastAddedKey = newlyAddedKeys[newlyAddedKeys.length - 1];
        const container = scrollContainerRef.current;

        if (!container) {
            return undefined;
        }

        let observer: MutationObserver | null = null;
        let timeoutId: number | undefined;

        const disconnectObserver = (): void => {
            observer?.disconnect();
            observer = null;
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
                timeoutId = undefined;
            }
        };

        const scrollToSection = (): boolean => {
            const section = getSectionElement(lastAddedKey);

            if (!section) {
                return false;
            }

            if (isElementFullyVisibleInContainer(section, container)) {
                return true;
            }

            scrollElementIntoContainerView(section, container);
            return true;
        };

        const frame = requestAnimationFrame(() => {
            if (scrollToSection()) {
                return;
            }

            observer = new MutationObserver(() => {
                if (scrollToSection()) {
                    disconnectObserver();
                }
            });

            observer.observe(container, { childList: true, subtree: true });

            timeoutId = window.setTimeout(() => {
                disconnectObserver();
            }, SECTION_MOUNT_TIMEOUT_MS);
        });

        return () => {
            cancelAnimationFrame(frame);
            disconnectObserver();
        };
    }, [dirtyFilterKeysSerialized, scrollContainerRef, getSectionElement]);
};
